import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import { dateFr } from '../modele.js';
import { euros } from '../supplements/CarteSupplements.js';
import { DesignationDetaillee } from './Designation.js';
import type { Catalogue, Devis, FactureDetaillee, LigneNouveauDevis, NouveauDevis, SourceDevis } from './source.js';

/**
 * Devis et factures à lignes détaillées sur D08 (30/10/2026). Sous chaque ligne, la désignation complète (code, intitulé, sections
 * en gras, sous-lignes), pliée si elle est longue. Un devis se crée depuis une prestation du catalogue versionné (le serveur fige
 * la copie du détail) ou depuis une ligne libre dont on saisit l'intitulé, les sections et les sous-lignes. Le prix est toujours
 * saisi : le catalogue ne porte aucun tarif. Le PDF de chaque devis et de chaque facture se demande au serveur.
 */
export interface PropsCarteDevis {
  source: SourceDevis;
  peutEcrire: boolean;
  factures: readonly FactureDetaillee[];
}

const LIBRE = 'libre';
const STATUTS: Record<Devis['status'], string> = { brouillon: 'Brouillon', envoye: 'Envoyé', signe: 'Signé', refuse: 'Refusé' };
const PORTEES: { code: NouveauDevis['phaseScope']; libelle: string }[] = [
  { code: 'jusqu_indivision', libelle: 'Jusqu’à l’indivision' },
  { code: 'partage', libelle: 'Partage' },
];

interface SectionSaisie {
  titre: string;
  lignes: string;
}
interface LigneSaisie {
  choix: string;
  intitule: string;
  sections: SectionSaisie[];
  montant: string;
}
const LIGNE_VIDE: LigneSaisie = { choix: '', intitule: '', sections: [], montant: '' };

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: `1px solid ${COULEURS.bordureDouce}`,
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minWidth: 0,
};
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  width: '100%',
  boxSizing: 'border-box',
};
const etiquette: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 600, fontSize: 14 };
const cellule: CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  verticalAlign: 'top',
  borderTop: `1px solid ${COULEURS.bordureDouce}`,
};
const celluleMontant: CSSProperties = { ...cellule, textAlign: 'right', whiteSpace: 'nowrap' };

/** Montant saisi en euros (« 2 500 », « 50,50 ») vers des centimes entiers, zéro compris ; null si illisible. */
export function centimesSaisis(saisie: string): number | null {
  const t = saisie.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

const messageDe = (e: unknown): string =>
  e instanceof ErreurApi
    ? e.code === 'VALIDATION_FAILED' && e.detail
      ? e.detail
      : e.messageUtilisateur
    : 'Une erreur est survenue. Réessayez.';

/** Corps de la création, ou le message qui dit quoi corriger. */
export function corpsDuDevis(
  phaseScope: NouveauDevis['phaseScope'],
  lignes: readonly LigneSaisie[],
): { corps: NouveauDevis } | { erreur: string } {
  const sortie: LigneNouveauDevis[] = [];
  for (const [i, l] of lignes.entries()) {
    const n = i + 1;
    const amountCents = centimesSaisis(l.montant);
    if (l.choix === '') return { erreur: `Ligne ${n} : choisissez une prestation du catalogue ou une ligne libre.` };
    if (amountCents === null) return { erreur: `Ligne ${n} : saisissez le montant en euros (par exemple 2 500 ou 50,50).` };
    if (l.choix !== LIBRE) {
      sortie.push({ prestationCode: l.choix, amountCents });
      continue;
    }
    if (l.intitule.trim() === '') return { erreur: `Ligne ${n} : saisissez l’intitulé de la ligne libre.` };
    const detail = l.sections
      .map((s) => ({
        titre: s.titre.trim(),
        lignes: s.lignes
          .split('\n')
          .map((x) => x.trim())
          .filter((x) => x !== ''),
      }))
      .filter((s) => s.titre !== '');
    sortie.push({ intitule: l.intitule.trim(), detail, amountCents });
  }
  return { corps: { phaseScope, lignes: sortie } };
}

function LienPdf({ url }: { url: string | undefined }): ReactElement | null {
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" style={{ color: COULEURS.indigo }}>
      Ouvrir le PDF
    </a>
  ) : null;
}

function TableLignes({
  lignes,
  total,
}: {
  lignes: { cle: string; designation: ReactElement; montant: number }[];
  total: number;
}): ReactElement {
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th scope="col" style={cellule}>
                Désignation
              </th>
              <th scope="col" style={celluleMontant}>
                Montant
              </th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={l.cle}>
                <td style={cellule}>{l.designation}</td>
                <td style={celluleMontant}>{euros(l.montant)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ margin: 0, textAlign: 'right', fontWeight: 600 }}>Total : {euros(total)}</p>
    </>
  );
}

export function CarteDevis({ source, peutEcrire, factures }: PropsCarteDevis): ReactElement {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [devis, setDevis] = useState<Devis[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [pdfs, setPdfs] = useState<Record<string, string>>({});
  const [portee, setPortee] = useState<NouveauDevis['phaseScope']>('jusqu_indivision');
  const [lignes, setLignes] = useState<LigneSaisie[]>([LIGNE_VIDE]);
  const [aConfirmer, setAConfirmer] = useState<NouveauDevis | null>(null);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [statut, setStatut] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([source.catalogue(), source.lister()]);
      setCatalogue(c);
      setDevis(d);
      setErreur(null);
    } catch (e) {
      setErreur(messageDe(e));
    }
  }, [source]);
  useEffect(() => {
    void charger();
  }, [charger]);

  const demanderPdf = async (cle: string, appel: () => Promise<{ downloadUrl: string }>) => {
    setOccupe(true);
    try {
      const { downloadUrl } = await appel();
      setPdfs((p) => ({ ...p, [cle]: downloadUrl }));
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setOccupe(false);
    }
  };

  const majLigne = (i: number, patch: Partial<LigneSaisie>) => setLignes((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const majSection = (i: number, k: number, patch: Partial<SectionSaisie>) =>
    setLignes((ls) => ls.map((l, j) => (j === i ? { ...l, sections: l.sections.map((s, m) => (m === k ? { ...s, ...patch } : s)) } : l)));

  const preparer = () => {
    const r = corpsDuDevis(portee, lignes);
    if ('erreur' in r) {
      setErreurForm(r.erreur);
      return;
    }
    setErreurForm(null);
    setAConfirmer(r.corps);
  };
  const confirmer = async () => {
    if (!aConfirmer) return;
    setOccupe(true);
    try {
      await source.creer(aConfirmer);
      setAConfirmer(null);
      setLignes([LIGNE_VIDE]);
      setStatut('Le devis est créé : le détail de chaque ligne y est figé.');
      await charger();
    } catch (e) {
      setErreurForm(messageDe(e));
      setAConfirmer(null);
    } finally {
      setOccupe(false);
    }
  };

  const prestation = (code: string) => catalogue?.prestations.find((p) => p.code === code);

  return (
    <section style={carte} aria-labelledby="titre-devis-detailles">
      <h2 id="titre-devis-detailles" style={{ margin: 0 }}>
        Devis et factures détaillés
      </h2>
      {erreur ? <p role="alert">{erreur}</p> : null}
      {statut ? <p role="status">{statut}</p> : null}
      {devis === null && !erreur ? <p role="status">Chargement des devis en cours…</p> : null}
      {devis && devis.length === 0 ? <p>Aucun devis n’est encore établi pour ce dossier.</p> : null}
      {devis?.map((d) => (
        <article key={d.id} data-devis={d.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            Devis du {dateFr(d.createdAt)} · {STATUTS[d.status]}
          </h3>
          <TableLignes
            total={d.total.amountCents}
            lignes={[...d.lines]
              .sort((a, b) => a.position - b.position)
              .map((l) => ({
                cle: l.id,
                designation: <DesignationDetaillee designation={l.designation} />,
                montant: l.amount.amountCents,
              }))}
          />
          <p style={{ margin: 0, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={styleBouton}
              disabled={occupe}
              aria-label={`PDF du devis du ${dateFr(d.createdAt)}`}
              onClick={() => void demanderPdf(d.id, () => source.pdfDevis(d.id))}
            >
              Préparer le PDF
            </button>
            <LienPdf url={pdfs[d.id]} />
          </p>
        </article>
      ))}

      {factures.length > 0 ? <h3 style={{ margin: 0 }}>Factures émises</h3> : null}
      {factures.map((f) => (
        <article key={f.id} data-facture={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h4 style={{ margin: 0 }}>
            Facture {f.number} · émise le {dateFr(f.issuedAt)}
          </h4>
          <TableLignes
            total={f.total.amountCents}
            lignes={(f.lines ?? []).map((l, i) => ({
              cle: `${f.id}-${i}`,
              designation: <DesignationDetaillee designation={l.designation} libelle={l.label} />,
              montant: l.amount.amountCents,
            }))}
          />
          <p style={{ margin: 0, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              style={styleBouton}
              disabled={occupe}
              aria-label={`PDF de la facture ${f.number}`}
              onClick={() => void demanderPdf(f.id, () => source.pdfFacture(f.id))}
            >
              Préparer le PDF
            </button>
            <LienPdf url={pdfs[f.id]} />
          </p>
        </article>
      ))}

      {peutEcrire ? (
        <div data-nouveau-devis="" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h3 style={{ margin: 0 }}>Nouveau devis</h3>
          {catalogue?.status === 'a_valider' ? (
            <p style={{ margin: 0, color: COULEURS.ardoise }}>
              Catalogue des prestations version {catalogue.version}, à valider par Isabelle.
            </p>
          ) : null}
          <label style={etiquette}>
            Portée du devis
            <select
              name="devis-portee"
              value={portee}
              disabled={occupe}
              style={champ}
              onChange={(e) => setPortee(e.target.value as NouveauDevis['phaseScope'])}
            >
              {PORTEES.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.libelle}
                </option>
              ))}
            </select>
          </label>
          {lignes.map((l, i) => {
            const p = l.choix !== LIBRE ? prestation(l.choix) : undefined;
            return (
              <fieldset
                key={i}
                style={{ border: `1px solid ${COULEURS.bordureDouce}`, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <legend>Ligne {i + 1}</legend>
                <label style={etiquette}>
                  Prestation
                  <select
                    name={`ligne-${i}-prestation`}
                    value={l.choix}
                    disabled={occupe}
                    style={champ}
                    onChange={(e) => majLigne(i, { choix: e.target.value })}
                  >
                    <option value="">Choisir une prestation</option>
                    {catalogue?.prestations.map((x) => (
                      <option key={x.code} value={x.code}>
                        {x.code} · {x.intitule}
                      </option>
                    ))}
                    <option value={LIBRE}>Ligne libre (détail saisi)</option>
                  </select>
                </label>
                {p ? <DesignationDetaillee designation={{ ...p, catalogueVersion: catalogue?.version ?? null }} /> : null}
                {l.choix === LIBRE ? (
                  <>
                    <label style={etiquette}>
                      Intitulé
                      <input
                        name={`ligne-${i}-intitule`}
                        maxLength={300}
                        value={l.intitule}
                        disabled={occupe}
                        style={champ}
                        onChange={(e) => majLigne(i, { intitule: e.target.value })}
                      />
                    </label>
                    {l.sections.map((s, k) => (
                      <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 12 }}>
                        <label style={etiquette}>
                          Section {k + 1} (en gras)
                          <input
                            name={`ligne-${i}-section-${k}-titre`}
                            maxLength={200}
                            value={s.titre}
                            disabled={occupe}
                            style={champ}
                            onChange={(e) => majSection(i, k, { titre: e.target.value })}
                          />
                        </label>
                        <label style={etiquette}>
                          Sous-lignes, une par ligne
                          <textarea
                            name={`ligne-${i}-section-${k}-lignes`}
                            rows={3}
                            value={s.lignes}
                            disabled={occupe}
                            style={champ}
                            onChange={(e) => majSection(i, k, { lignes: e.target.value })}
                          />
                        </label>
                        <button
                          type="button"
                          style={styleBouton}
                          disabled={occupe}
                          onClick={() => majLigne(i, { sections: l.sections.filter((_, m) => m !== k) })}
                        >
                          Retirer la section {k + 1}
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      style={styleBouton}
                      disabled={occupe}
                      onClick={() => majLigne(i, { sections: [...l.sections, { titre: '', lignes: '' }] })}
                    >
                      Ajouter une section
                    </button>
                  </>
                ) : null}
                <label style={etiquette}>
                  Montant (€)
                  <input
                    name={`ligne-${i}-montant`}
                    inputMode="decimal"
                    value={l.montant}
                    disabled={occupe}
                    style={champ}
                    onChange={(e) => majLigne(i, { montant: e.target.value })}
                  />
                </label>
                {lignes.length > 1 ? (
                  <button
                    type="button"
                    style={styleBouton}
                    disabled={occupe}
                    onClick={() => setLignes((ls) => ls.filter((_, j) => j !== i))}
                  >
                    Retirer la ligne {i + 1}
                  </button>
                ) : null}
              </fieldset>
            );
          })}
          <button type="button" style={styleBouton} disabled={occupe} onClick={() => setLignes((ls) => [...ls, LIGNE_VIDE])}>
            Ajouter une ligne
          </button>
          {erreurForm ? <p role="alert">{erreurForm}</p> : null}
          <button type="button" style={styleBoutonPrincipal} disabled={occupe} onClick={preparer}>
            Créer le devis
          </button>
          {aConfirmer ? (
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="titre-confirmer-devis"
              style={{ border: `1px solid ${COULEURS.ambre}`, background: COULEURS.ambreFond, borderRadius: 8, padding: 12 }}
            >
              <h4 id="titre-confirmer-devis" style={{ marginTop: 0 }}>
                Confirmez-vous ce devis ?
              </h4>
              <p>
                {aConfirmer.lignes.length} ligne{aConfirmer.lignes.length > 1 ? 's' : ''}, total{' '}
                {euros(aConfirmer.lignes.reduce((s, l) => s + l.amountCents, 0))}. Le détail de chaque ligne sera figé : un changement du
                catalogue ne modifiera pas ce devis.
              </p>
              <button type="button" style={styleBoutonPrincipal} disabled={occupe} autoFocus onClick={() => void confirmer()}>
                Confirmer
              </button>{' '}
              <button type="button" style={styleBouton} onClick={() => setAConfirmer(null)}>
                Annuler
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
